import { useCallback, useEffect, useState } from "react";
import { KeyRound, LifeBuoy, LineChart, RotateCcw, ShieldAlert, Snowflake, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { ADMIN_NAV } from "@/pages/admin/nav";
import { adminService } from "@/services/adminService";
import { errorMessage } from "@/services/apiClient";

const card = "rounded-md border border-[#22252F] bg-[#0E1015]";
const input = "rounded-md border border-[#22252F] bg-[#12141A] px-3 py-2 text-sm text-white";

const money = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(2)} USD`);
const when = (iso) => (iso ? new Date(iso).toLocaleString("pt-PT") : "—");

export default function AdminSupAuditPage() {
  const [data, setData] = useState(null);
  const [view, setView] = useState("actions");
  const [hidden, setHidden] = useState([]);
  const [hiddenPicked, setHiddenPicked] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [selected, setSelected] = useState([]);
  const [filters, setFilters] = useState({ actor: "", action: "", date_from: "", date_to: "" });
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    try {
      setData(await adminService.supAudit({ ...filters, page, per_page: 25 }));
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, [filters, page]);

  const loadHidden = useCallback(async () => {
    try {
      setHidden(await adminService.supHidden());
      setSupervisors(await adminService.supervisors());
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadHidden(); }, [loadHidden]);

  const restore = async (row) => {
    try {
      await adminService.restoreHidden(row.id);
      toast.success(`${row.name} reposto`);
      loadHidden();
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const purgeHiddenRecord = async (row) => {
    if (!window.confirm(`Eliminar definitivamente ${row.name}? A conta e todos os dados serão apagados.`)) return;
    try {
      await adminService.purgeHidden(row.id);
      toast.success(`${row.name} eliminado definitivamente`);
      loadHidden();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const bulkHidden = async (action) => {
    const label = action === "restore"
      ? `Repor ${hiddenPicked.length} registo(s) selecionado(s)?`
      : `Eliminar definitivamente ${hiddenPicked.length} registo(s)? As contas e os dados serão apagados.`;
    if (!window.confirm(label)) return;
    try {
      const res = await adminService.bulkHidden(hiddenPicked, action);
      toast.success(`${res.affected} registo(s) ${action === "restore" ? "reposto(s)" : "eliminado(s)"}`);
      setHiddenPicked([]);
      loadHidden();
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const toggleSupervisor = async (s) => {    const next = s.status === "suspended" ? "active" : "suspended";
    try {
      await adminService.setSupervisorStatus(s.id, next);
      toast.success(next === "suspended" ? `${s.name} congelado` : `${s.name} reativado`);
      loadHidden();
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const toggleSupport = async (s) => {
    try {
      await adminService.setSupervisorSupport(s.id, !s.support_enabled);
      toast.success(!s.support_enabled ? `${s.name} recebe os pedidos da equipa` : `Suporte desligado para ${s.name}`);
      loadHidden();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const purge = async (payload, confirmText) => {
    if (!window.confirm(confirmText)) return;
    try {
      const res = await adminService.purgeAudit(payload);
      toast.success(`${res.deleted} registo(s) apagado(s)`);
      setSelected([]);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const toggleTrading = async (s) => {
    try {
      await adminService.setSupervisorTrading(s.id, !s.can_trade);
      toast.success(!s.can_trade ? `${s.name} pode editar trading` : `Trading bloqueado para ${s.name}`);
      loadHidden();
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const changePassword = async (s) => {
    const value = window.prompt(`Nova palavra-passe para ${s.name} (mín. 6 caracteres)`);
    if (!value) return;
    try {
      await adminService.setSupervisorPassword(s.id, value);
      toast.success("Palavra-passe alterada");
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const set = (key) => (e) => { setPage(1); setFilters({ ...filters, [key]: e.target.value }); };

  return (
    <AppShell nav={ADMIN_NAV} title="Auditoria de Supervisores"
              subtitle="Todas as operações de dinheiro, documentos e registos feitas pelos Supervisores">
      <div data-testid="sup-audit-page" className="space-y-6">
        <div className="flex gap-2">
          {[{ k: "actions", l: "Ações" },
            { k: "hidden", l: `Eliminados${hidden.length ? ` (${hidden.length})` : ""}` },
            { k: "supervisors", l: `Supervisores${supervisors.length ? ` (${supervisors.length})` : ""}` }].map((t) => (
            <button key={t.k} data-testid={`audit-view-${t.k}`} onClick={() => setView(t.k)}
                    className={`rounded-full px-4 py-1.5 text-sm transition-colors ${
                      view === t.k ? "bg-[#2563EB] text-white" : "border border-[#22252F] text-[#94A3B8] hover:text-white"}`}>
              {t.l}
            </button>
          ))}
        </div>

        {view === "supervisors" ? (
          <div className={`${card} overflow-x-auto`} data-testid="supervisors-list">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead className="border-b border-[#22252F] text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">
                <tr>
                  <th className="px-4 py-3">Supervisor</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Brand</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3 text-right">Clientes</th>
                  <th className="px-4 py-3 text-right">Ações registadas</th>
                  <th className="px-4 py-3">Último acesso</th>
                  <th className="px-4 py-3 text-right">Gestão</th>
                </tr>
              </thead>
              <tbody>
                {supervisors.map((s) => (
                  <tr key={s.id} data-testid={`supervisor-row-${s.id}`} className="border-b border-[#1A1D25]">
                    <td className="px-4 py-3 text-white">{s.name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-[#94A3B8]">{s.email || "—"}</td>
                    <td className="px-4 py-3 text-[#60A5FA]">{s.brand_name}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded border px-2 py-0.5 font-mono text-[10px] uppercase ${
                        s.status === "suspended"
                          ? "border-[#EF4444]/40 bg-[#EF4444]/10 text-[#F87171]"
                          : "border-[#10B981]/40 bg-[#10B981]/10 text-[#34D399]"}`}>
                        {s.status === "suspended" ? "Congelado" : "Ativo"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-white">{s.clients}</td>
                    <td className="px-4 py-3 text-right font-mono text-[#94A3B8]">{s.actions}</td>
                    <td className="px-4 py-3 font-mono text-xs text-[#94A3B8]">{when(s.last_login_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button data-testid={`supervisor-support-${s.id}`} onClick={() => toggleSupport(s)}
                                className={`inline-flex items-center gap-1 rounded-md border px-3 py-1 text-xs ${
                                  s.support_enabled
                                    ? "border-[#2563EB]/50 bg-[#2563EB]/10 text-[#93C5FD]"
                                    : "border-[#22252F] text-[#94A3B8] hover:border-[#60A5FA]/50"}`}>
                          <LifeBuoy className="h-3.5 w-3.5" /> {s.support_enabled ? "Suporte ligado" : "Suporte desligado"}
                        </button>
                        <button data-testid={`supervisor-trading-${s.id}`} onClick={() => toggleTrading(s)}
                                className={`inline-flex items-center gap-1 rounded-md border px-3 py-1 text-xs ${
                                  s.can_trade
                                    ? "border-[#10B981]/50 bg-[#10B981]/10 text-[#34D399]"
                                    : "border-[#22252F] text-[#94A3B8] hover:border-[#60A5FA]/50"}`}>
                          <LineChart className="h-3.5 w-3.5" /> {s.can_trade ? "Trading ligado" : "Trading desligado"}
                        </button>
                        <button data-testid={`supervisor-toggle-${s.id}`} onClick={() => toggleSupervisor(s)}
                                className={`inline-flex items-center gap-1 rounded-md border px-3 py-1 text-xs ${
                                  s.status === "suspended"
                                    ? "border-[#22252F] text-[#10B981] hover:border-[#10B981]/50"
                                    : "border-[#22252F] text-[#F59E0B] hover:border-[#F59E0B]/50"}`}>
                          <Snowflake className="h-3.5 w-3.5" /> {s.status === "suspended" ? "Reativar" : "Congelar"}
                        </button>
                        <button data-testid={`supervisor-password-${s.id}`} onClick={() => changePassword(s)}
                                className="inline-flex items-center gap-1 rounded-md border border-[#22252F] px-3 py-1 text-xs text-[#60A5FA] hover:border-[#60A5FA]/50">
                          <KeyRound className="h-3.5 w-3.5" /> Palavra-passe
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!supervisors.length ? (
              <p className="flex items-center justify-center gap-2 py-10 text-sm text-[#64748B]">
                <ShieldAlert className="h-4 w-4" /> Nenhum Supervisor criado
              </p>
            ) : null}
            <p className="border-t border-[#22252F] px-4 py-3 text-xs text-[#64748B]">
              Congelar bloqueia o acesso dele imediatamente. Clientes, gestores e agentes que ele criou
              mantêm-se intactos. Um Supervisor nunca pode ser promovido a Administrador.
            </p>
          </div>
        ) : null}

        {view === "hidden" ? (
          <div className={`${card} overflow-x-auto`} data-testid="hidden-list">
            {hiddenPicked.length ? (
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#22252F] px-4 py-3"
                   data-testid="hidden-bulk-bar">
                <span className="font-mono text-xs text-[#94A3B8]">
                  {hiddenPicked.length} selecionado(s)
                </span>
                <div className="flex gap-2">
                  <button data-testid="hidden-bulk-restore" onClick={() => bulkHidden("restore")}
                          className="inline-flex items-center gap-2 rounded-md border border-[#22252F] px-3 py-1.5 text-xs text-[#10B981] hover:border-[#10B981]/50">
                    <RotateCcw className="h-3.5 w-3.5" /> Manter selecionados
                  </button>
                  <button data-testid="hidden-bulk-purge" onClick={() => bulkHidden("purge")}
                          className="inline-flex items-center gap-2 rounded-md border border-[#22252F] px-3 py-1.5 text-xs text-[#F87171] hover:border-[#EF4444]/50">
                    <Trash2 className="h-3.5 w-3.5" /> Eliminar selecionados de vez
                  </button>
                  <button data-testid="hidden-bulk-clear" onClick={() => setHiddenPicked([])}
                          className="rounded-md border border-[#22252F] px-3 py-1.5 text-xs text-[#94A3B8] hover:text-white">
                    Limpar
                  </button>
                </div>
              </div>
            ) : null}
            <table className="w-full min-w-[860px] text-left text-sm">
              <thead className="border-b border-[#22252F] text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">
                <tr>
                  <th className="px-4 py-3">
                    <input type="checkbox" data-testid="hidden-select-all"
                           checked={!!hidden.length && hiddenPicked.length === hidden.length}
                           onChange={(e) => setHiddenPicked(e.target.checked ? hidden.map((h) => h.id) : [])} />
                  </th>
                  <th className="px-4 py-3">Registo</th>
                  <th className="px-4 py-3">Perfil</th>
                  <th className="px-4 py-3">Contacto</th>
                  <th className="px-4 py-3 text-right">Saldo</th>
                  <th className="px-4 py-3">Eliminado por</th>
                  <th className="px-4 py-3">Quando</th>
                  <th className="px-4 py-3 text-right">Ação</th>
                </tr>
              </thead>
              <tbody>
                {hidden.map((h) => (
                  <tr key={h.id} data-testid={`hidden-row-${h.id}`} className="border-b border-[#1A1D25]">
                    <td className="px-4 py-3">
                      <input type="checkbox" data-testid={`hidden-pick-${h.id}`}
                             checked={hiddenPicked.includes(h.id)}
                             onChange={(e) => setHiddenPicked((prev) =>
                               e.target.checked ? [...prev, h.id] : prev.filter((i) => i !== h.id))} />
                    </td>
                    <td className="px-4 py-3 text-white">{h.name}</td>
                    <td className="px-4 py-3 font-mono text-xs uppercase text-[#60A5FA]">{h.role}</td>
                    <td className="px-4 py-3 font-mono text-xs text-[#94A3B8]">{h.email || h.phone_full || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono text-white">{money(h.virtual_balance)}</td>
                    <td className="px-4 py-3 text-[#F87171]">{h.hidden_by || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-[#94A3B8]">{when(h.hidden_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button data-testid={`restore-${h.id}`} onClick={() => restore(h)}
                                className="inline-flex items-center gap-2 rounded-md border border-[#22252F] px-3 py-1 text-xs text-[#10B981] hover:border-[#10B981]/50">
                          <RotateCcw className="h-3.5 w-3.5" /> Manter
                        </button>
                        <button data-testid={`purge-${h.id}`} onClick={() => purgeHiddenRecord(h)}
                                className="inline-flex items-center gap-2 rounded-md border border-[#22252F] px-3 py-1 text-xs text-[#F87171] hover:border-[#EF4444]/50">
                          <Trash2 className="h-3.5 w-3.5" /> Eliminar de vez
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!hidden.length ? (
              <p className="flex items-center justify-center gap-2 py-10 text-sm text-[#64748B]">
                <ShieldAlert className="h-4 w-4" /> Nenhum registo eliminado pelos Supervisores
              </p>
            ) : null}
          </div>
        ) : null}

        {view === "actions" ? (
        <>
        <div className="flex flex-wrap gap-3">
          <select data-testid="audit-filter-actor" value={filters.actor} onChange={set("actor")} className={input}>
            <option value="">Todos os supervisores</option>
            {(data?.supervisors || []).map((s) => (
              <option key={s.id} value={s.id}>{s.name} {s.email ? `· ${s.email}` : ""}</option>
            ))}
          </select>
          <select data-testid="audit-filter-action" value={filters.action} onChange={set("action")} className={input}>
            <option value="">Todas as ações</option>
            {(data?.actions || []).map((a) => (
              <option key={a.value} value={a.value}>{a.label}</option>
            ))}
          </select>
          <input type="date" data-testid="audit-filter-from" value={filters.date_from}
                 onChange={set("date_from")} className={input} />
          <input type="date" data-testid="audit-filter-to" value={filters.date_to}
                 onChange={set("date_to")} className={input} />
          <button data-testid="audit-purge-selected" disabled={!selected.length}
                  onClick={() => purge({ ids: selected }, `Apagar ${selected.length} registo(s) selecionado(s)?`)}
                  className="inline-flex h-10 items-center gap-2 rounded-md border border-[#EF4444]/40 bg-[#EF4444]/10 px-3 text-sm text-[#F87171] disabled:opacity-40">
            <Trash2 className="h-4 w-4" /> Apagar selecionados ({selected.length})
          </button>
          <button data-testid="audit-purge-category" disabled={!filters.action}
                  onClick={() => purge({ action: filters.action },
                                       "Apagar TODOS os registos desta categoria? Esta ação não pode ser desfeita.")}
                  className="inline-flex h-10 items-center gap-2 rounded-md border border-[#EF4444]/40 px-3 text-sm text-[#F87171] disabled:opacity-40">
            <Trash2 className="h-4 w-4" /> Apagar categoria
          </button>
          <button data-testid="audit-purge-30"
                  onClick={() => purge({ older_than_days: 30 }, "Apagar registos com mais de 30 dias?")}
                  className="inline-flex h-10 items-center gap-2 rounded-md border border-[#22252F] px-3 text-sm text-[#94A3B8] hover:text-white">
            +30 dias
          </button>
          <button data-testid="audit-purge-90"
                  onClick={() => purge({ older_than_days: 90 }, "Apagar registos com mais de 90 dias?")}
                  className="inline-flex h-10 items-center gap-2 rounded-md border border-[#22252F] px-3 text-sm text-[#94A3B8] hover:text-white">
            +90 dias
          </button>
        </div>

        {data?.by_action?.length ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="audit-totals">
            {data.by_action.map((b) => (
              <div key={b.action} className={`${card} p-4`}>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">{b.action}</p>
                <p className="mt-1 font-mono text-lg text-white">{money(b.total)}</p>
                <p className="font-mono text-xs text-[#64748B]">{b.count} operação(ões)</p>
              </div>
            ))}
          </div>
        ) : null}

        <div className={`${card} overflow-x-auto`} data-testid="audit-list">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead className="border-b border-[#22252F] text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">
              <tr>
                <th className="px-4 py-3">
                  <input type="checkbox" data-testid="audit-select-all"
                         checked={!!data?.items?.length && selected.length === data.items.length}
                         onChange={(e) => setSelected(e.target.checked ? (data?.items || []).map((i) => i.id) : [])} />
                </th>
                <th className="px-4 py-3">Quando</th>
                <th className="px-4 py-3">Supervisor</th>
                <th className="px-4 py-3">Ação</th>
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3 text-right">Valor</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Motivo</th>
                <th className="px-4 py-3">IP</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).map((r) => (
                <tr key={r.id} data-testid={`audit-row-${r.id}`} className="border-b border-[#1A1D25]">
                  <td className="px-4 py-3">
                    <input type="checkbox" data-testid={`audit-select-${r.id}`}
                           checked={selected.includes(r.id)}
                           onChange={(e) => setSelected(e.target.checked
                             ? [...selected, r.id]
                             : selected.filter((s) => s !== r.id))} />
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-[#94A3B8]">{when(r.created_at)}</td>
                  <td className="px-4 py-3 text-white">{r.actor_name}</td>
                  <td className="px-4 py-3 text-[#60A5FA]">{r.action_label}</td>
                  <td className="px-4 py-3 text-[#CBD5E1]">{r.target_name || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono text-white">{money(r.amount)}</td>
                  <td className="px-4 py-3 font-mono text-xs uppercase text-[#F59E0B]">{r.kind_label || "—"}</td>
                  <td className="px-4 py-3 text-xs text-[#94A3B8]">{r.reason || r.detail || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[#64748B]">{r.ip || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!data?.items?.length ? (
            <p className="flex items-center justify-center gap-2 py-10 text-sm text-[#64748B]">
              <ShieldAlert className="h-4 w-4" /> Sem registos para estes filtros
            </p>
          ) : null}
        </div>

        {data?.pages > 1 ? (
          <div className="flex items-center gap-3">
            <button data-testid="audit-prev" disabled={page <= 1} onClick={() => setPage(page - 1)}
                    className={`${input} disabled:opacity-40`}>Anterior</button>
            <span className="font-mono text-xs text-[#94A3B8]">{page} / {data.pages}</span>
            <button data-testid="audit-next" disabled={page >= data.pages} onClick={() => setPage(page + 1)}
                    className={`${input} disabled:opacity-40`}>Seguinte</button>
          </div>
        ) : null}
        </>
        ) : null}
      </div>
    </AppShell>
  );
}
